/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, makeObservable, observable } from 'mobx';

import type { GridExpandCollapsePluginMeta } from '@veeam/components/lib/GridCore/plugins/expandCollapse/types/GridExpandCollapsePluginMeta';

import type { ExpandableItem, GridProps, GridStorageActions } from '../../../interfaces';
import type { GridStore } from '../grid-store';

export class ExpandedRowsStore<RowDataT, TotalRowDataT, FilterT> {
    @observable expandedIds: string[] = [];

    meta: GridExpandCollapsePluginMeta[] = [];
    grouping: string[] = [];
    expandableConfig: GridProps<RowDataT, TotalRowDataT, FilterT>['expandable'];

    constructor(
        private gridStore: GridStore<RowDataT, TotalRowDataT, FilterT>,
        props: GridProps<RowDataT, TotalRowDataT, FilterT>,
        private gridStorageActions: GridStorageActions<FilterT> | null
    ) {
        makeObservable(this);

        this.grouping = props.expandable?.grouping;
        this.expandableConfig = props.expandable;
    }

    get isExpandableGrid(): boolean {
        return !!this.expandableConfig;
    }

    @action.bound
    setExpanded(id: string, index: number): void {
        if (this.expandedIds.includes(id)) {
            this.meta[index].isExpanded = false;
            this.expandedIds = this.expandedIds.filter(item => item !== id);
        } else {
            this.expandedIds.push(id);
            this.meta[index].isExpanded = true;
        }
    }

    transformExpandableDataToFlatList(data: ExpandableItem<RowDataT>[]): RowDataT[] {
        const allData: RowDataT[] = [];
        const allMeta: GridExpandCollapsePluginMeta[] = [];

        data.forEach((item) => {
            const { data: itemData, meta: itemMeta } = this.transformItem(item, 0);

            allData.push(...itemData);
            allMeta.push(...itemMeta);
        });

        this.meta = allMeta;

        return allData;
    }

    transformItem(item: ExpandableItem<RowDataT>, depth: number) {
        const resultData: RowDataT[] = [];
        const resultMeta: GridExpandCollapsePluginMeta[] = [];

        resultData.push(item.data);

        const meta: GridExpandCollapsePluginMeta = {
            isExpandable: (item.children && item.children.length > 0) ?? false,
            isExpanded: this.expandedIds.includes(item.data[this.gridStore.selectionStore.selection.field]),
            depth,
        };

        resultMeta.push(meta);

        if (meta.isExpandable && meta.isExpanded) {
            item.children?.forEach((child) => {
                const { data: childData, meta: childMeta } = this.transformItem(child, depth + 1);

                resultData.push(...childData);
                resultMeta.push(...childMeta);
            });
        }

        return { data: resultData, meta: resultMeta };
    }
}
